import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { db } from '../data/store';
import { collection, doc, setDoc, deleteDoc, onSnapshot, query, orderBy, newId } from '@battleops/localstore';

const MilestoneContext = createContext();
const MILESTONES_COLLECTION = 'milestones';
const GOALS_COLLECTION = 'goals';

const initialState = { milestones: [], goals: [], loaded: false };

function reducer(state, action) {
  switch (action.type) {
    case 'LOAD_MILESTONES':
      return { ...state, milestones: action.milestones, loaded: true };
    case 'SET_MILESTONES':
      return { ...state, milestones: action.milestones };
    case 'LOAD_GOALS':
      return { ...state, goals: action.goals };
    case 'SET_GOALS':
      return { ...state, goals: action.goals };
    default:
      return state;
  }
}

export function MilestoneProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const qm = query(collection(db, MILESTONES_COLLECTION), orderBy('targetDate', 'asc'));
    const unsub1 = onSnapshot(qm, (snap) => {
      dispatch({ type: 'LOAD_MILESTONES', milestones: snap.docs.map(d => ({ id: d.id, ...d.data() })) });
    }, () => dispatch({ type: 'LOAD_MILESTONES', milestones: [] }));

    const qg = query(collection(db, GOALS_COLLECTION), orderBy('createdAt', 'asc'));
    const unsub2 = onSnapshot(qg, (snap) => {
      dispatch({ type: 'LOAD_GOALS', goals: snap.docs.map(d => ({ id: d.id, ...d.data() })) });
    }, () => dispatch({ type: 'LOAD_GOALS', goals: [] }));

    return () => { unsub1(); unsub2(); };
  }, []);

  // Milestones
  const addMilestone = useCallback((m) => {
    const id = newId();
    const milestone = {
      id,
      title: m.title,
      description: m.description || '',
      projectId: m.projectId || null,
      targetDate: m.targetDate || null,
      completed: false,
      completedAt: null,
      createdAt: Date.now(),
    };
    dispatch({ type: 'SET_MILESTONES', milestones: [...state.milestones, milestone] });
    setDoc(doc(db, MILESTONES_COLLECTION, id), milestone);
  }, [state.milestones]);

  const updateMilestone = useCallback((updates) => {
    dispatch({ type: 'SET_MILESTONES', milestones: state.milestones.map(m => m.id === updates.id ? { ...m, ...updates } : m) });
    const full = state.milestones.find(m => m.id === updates.id);
    if (full) setDoc(doc(db, MILESTONES_COLLECTION, updates.id), { ...full, ...updates });
  }, [state.milestones]);

  const toggleMilestone = useCallback((id) => {
    const m = state.milestones.find(ms => ms.id === id);
    if (m) updateMilestone({ id, completed: !m.completed, completedAt: !m.completed ? Date.now() : null });
  }, [state.milestones, updateMilestone]);

  const deleteMilestone = useCallback((id) => {
    dispatch({ type: 'SET_MILESTONES', milestones: state.milestones.filter(m => m.id !== id) });
    deleteDoc(doc(db, MILESTONES_COLLECTION, id));
  }, [state.milestones]);

  const getProjectMilestones = useCallback((projectId) => {
    return state.milestones.filter(m => m.projectId === projectId);
  }, [state.milestones]);

  // Goals
  const addGoal = useCallback((g) => {
    const id = newId();
    const goal = {
      id,
      title: g.title,
      metric: g.metric, // 'tasks_completed' | 'hours_tracked' | 'money_earned' | 'projects_completed' | 'custom'
      targetValue: g.targetValue || 0,
      currentValue: g.currentValue || 0,
      unit: g.unit || '',
      deadline: g.deadline || null,
      completed: false,
      completedAt: null,
      createdAt: Date.now(),
    };
    dispatch({ type: 'SET_GOALS', goals: [...state.goals, goal] });
    setDoc(doc(db, GOALS_COLLECTION, id), goal);
  }, [state.goals]);

  const updateGoal = useCallback((updates) => {
    dispatch({ type: 'SET_GOALS', goals: state.goals.map(g => g.id === updates.id ? { ...g, ...updates } : g) });
    const full = state.goals.find(g => g.id === updates.id);
    if (full) setDoc(doc(db, GOALS_COLLECTION, updates.id), { ...full, ...updates });
  }, [state.goals]);

  const deleteGoal = useCallback((id) => {
    dispatch({ type: 'SET_GOALS', goals: state.goals.filter(g => g.id !== id) });
    deleteDoc(doc(db, GOALS_COLLECTION, id));
  }, [state.goals]);

  return (
    <MilestoneContext.Provider value={{
      milestones: state.milestones,
      goals: state.goals,
      loaded: state.loaded,
      addMilestone, updateMilestone, toggleMilestone, deleteMilestone, getProjectMilestones,
      addGoal, updateGoal, deleteGoal,
    }}>
      {children}
    </MilestoneContext.Provider>
  );
}

export function useMilestones() {
  const context = useContext(MilestoneContext);
  if (!context) throw new Error('useMilestones must be used within MilestoneProvider');
  return context;
}
